import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Add, InfoOutlined, KeyboardArrowDown, KeyboardArrowUp, RemoveCircleOutline } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import { OCR_GUIDE_ROW_HEIGHT, buildOutlinedErrorBorderStyles, mergeOutlinedInputStyles, receiptOutlinedInputStyles } from './AuditDynamicFormConstants';
import auditService from '../../services/auditService';
import { sanitizeVendingSalesIntegerInput } from '../../features/audit/fixOcr/utils/vendingSalesInputUtils';
import type { VendingSalesLine } from '../../types/vendingSalesFormTypes';
import {
  buildLegacyVendingInventoryGamePackValue,
  buildVendingInventoryGamePackValue,
  getVendingInventoryGameAfterAutoDashBackspace,
  getVendingInventoryGameIdLength,
  parseLegacyVendingInventoryGamePackInput,
  parseVendingInventoryGamePackInput,
} from '../../features/audit/fixOcr/utils/vendingInventoryGamePackInputUtils';
import {
  appendInventorySelectedPackSlot,
  getInventorySelectedPackAggregateQuantity,
  getInventorySelectedPackAggregateValue,
  getInventorySelectedPackDisplayValue,
  getInventorySelectedPackSlots,
  getInventoryPrimaryPackId,
  normalizeInventorySelectedPack,
  removeInventorySelectedPackSlot,
} from '../../features/audit/fixOcr/utils/vendingInventoryPackUtils';

type PackCandidate = {
  pack_id?: number | null;
  pack_location?: string | null;
  status?: string | null;
  status_date?: string | null;
  activated_date?: string | null;
  settled_date?: string | null;
  backstock_state?: string | null;
  last_backstock_location?: string | null;
  latest_remaining_qty?: number | null;
};

type VendingInventoryMultiPackRowsProps = {
  field: any;
  idx: number;
  line?: VendingSalesLine | null;
  linePathBase: string;
  rowErrors?: any;
  setValue: (name: string, value: unknown, options?: any) => void;
  remove: (index: number) => void;
  onInsert?: (index: number) => void;
  gamesData: Map<string, number> | null;
  reportType: 'vending_sales' | 'vending_inventory' | 'vending_shift_report';
  queueId?: string;
  reportDate?: string;
  auditStatus?: string;
  hoveredRowIndex: number | null;
  onRowHover?: (index: number | null) => void;
  isNewPack?: boolean;
};

const formatPackCandidateDate = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return '-';
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

const formatPackCandidateLocation = (candidate: PackCandidate): string => {
  const location = candidate?.pack_location || candidate?.last_backstock_location;
  if (!location) {
    return '-';
  }

  const normalized = String(location).trim();
  if (!normalized) {
    return '-';
  }

  const backstockState = String(candidate?.backstock_state || '').trim().toLowerCase();
  const backstockLocations = new Set(['cashier', 'locker', 'vending']);
  if (backstockState && backstockState !== 'none' && backstockLocations.has(normalized.toLowerCase())) {
    return `backstock ${normalized}`;
  }

  return normalized;
};

const toOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const VendingInventoryMultiPackRows: React.FC<VendingInventoryMultiPackRowsProps> = React.memo(({
  field,
  idx,
  line,
  linePathBase,
  rowErrors,
  setValue,
  remove,
  onInsert,
  gamesData,
  reportType,
  queueId,
  reportDate,
  auditStatus,
  hoveredRowIndex,
  onRowHover,
  isNewPack,
}) => {
  const [packMenuAnchorEl, setPackMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activePackIndex, setActivePackIndex] = useState<number>(0);
  const [dynamicPackCandidates, setDynamicPackCandidates] = useState<PackCandidate[] | null>(null);
  const [packRecommendationsLoading, setPackRecommendationsLoading] = useState(false);
  const [packRecommendationsError, setPackRecommendationsError] = useState<string | null>(null);

  const isInventoryReport = reportType === 'vending_inventory' || reportType === 'vending_shift_report';
  const isInventoryReady = isInventoryReport && String(auditStatus || '').toLowerCase() === 'ready';
  const usesLengthBasedGamePackInput = reportType === 'vending_inventory' || reportType === 'vending_shift_report';
  const canManagePackSlots = reportType === 'vending_inventory' || reportType === 'vending_shift_report';
  const canInsertInventoryBin = reportType === 'vending_inventory' && Boolean(onInsert);
  const isRowHovered = hoveredRowIndex === idx + 1;
  const packSlots = useMemo(() => getInventorySelectedPackSlots(line), [line]);
  const packCandidates = useMemo(() => {
    if (dynamicPackCandidates) {
      return dynamicPackCandidates;
    }

    const candidates = (line as any)?.pack_candidates;
    return Array.isArray(candidates) ? candidates : [];
  }, [dynamicPackCandidates, line]);

  const aggregateQuantity = useMemo(() => getInventorySelectedPackAggregateQuantity(line), [line]);
  const aggregateValue = useMemo(() => getInventorySelectedPackAggregateValue(line, gamesData), [gamesData, line]);

  const lineBin = line?.bin === null || line?.bin === undefined ? '' : String(line.bin).trim();
  const lineGame = line?.game === null || line?.game === undefined ? '' : String(line.game).trim();
  const primaryPackId = useMemo(() => getInventoryPrimaryPackId(line), [line]);
  const gameIdLength = useMemo(() => getVendingInventoryGameIdLength(gamesData), [gamesData]);
  const slotCount = packSlots.length;

  const handleRowHover = useCallback((nextHover: number | null) => {
    onRowHover?.(nextHover);
  }, [onRowHover]);

  useEffect(() => {
    if (slotCount > 0) {
      const requiredPackCount = Number(line?.required_pack_count ?? 0);
      if (requiredPackCount !== slotCount) {
        setValue(`${linePathBase}.required_pack_count`, slotCount, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }

    const nextSelectedPacks = packSlots.map((pack) => normalizeInventorySelectedPack(pack));
    const hasSelectedPacksArray = Array.isArray((line as any)?.selected_packs);
    const currentSelectedPacks = hasSelectedPacksArray
      ? (line as any).selected_packs.map((pack: any) => normalizeInventorySelectedPack(pack))
      : [];
    const currentSelectedPackId = (line as any)?.selected_pack_id ?? null;
    const currentSelectedPackIdHasValue = currentSelectedPackId !== null
      && currentSelectedPackId !== undefined
      && String(currentSelectedPackId).trim() !== '';
    const selectedPackIdFromSlots = nextSelectedPacks.find((pack) => (
      pack.pack_id !== null
      && pack.pack_id !== undefined
      && String(pack.pack_id).trim() !== ''
    ))?.pack_id ?? null;
    const nextSelectedPackId = selectedPackIdFromSlots ?? (
      (!hasSelectedPacksArray || currentSelectedPacks.length === 0) && currentSelectedPackIdHasValue
        ? currentSelectedPackId
        : null
    );
    const currentPackValue = (line as any)?.pack ?? null;

    if (Number(line?.quantity ?? -1) !== aggregateQuantity) {
      setValue(`${linePathBase}.quantity`, aggregateQuantity, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (Number(line?.value ?? -1) !== aggregateValue) {
      setValue(`${linePathBase}.value`, Number(aggregateValue.toFixed(2)), {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (String(currentSelectedPackId ?? '') !== String(nextSelectedPackId ?? '')) {
      setValue(`${linePathBase}.selected_pack_id`, nextSelectedPackId, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (String(currentPackValue ?? '') !== String(nextSelectedPackId ?? '')) {
      setValue(`${linePathBase}.pack`, nextSelectedPackId, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (JSON.stringify(currentSelectedPacks) !== JSON.stringify(nextSelectedPacks)) {
      setValue(`${linePathBase}.selected_packs`, nextSelectedPacks, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [aggregateQuantity, aggregateValue, line, linePathBase, packSlots, setValue, slotCount]);

  const handlePackInputChange = useCallback((packIndex: number, rawValue: string) => {
    const { game, packId } = usesLengthBasedGamePackInput
      ? parseVendingInventoryGamePackInput(rawValue, gameIdLength)
      : parseLegacyVendingInventoryGamePackInput(rawValue);

    setValue(`${linePathBase}.game`, game, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${linePathBase}.selected_packs.${packIndex}.pack_id`, packId, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${linePathBase}.selected_packs.${packIndex}.remaining_qty`, line?.selected_packs?.[packIndex]?.remaining_qty ?? null, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [gameIdLength, line?.selected_packs, linePathBase, setValue, usesLengthBasedGamePackInput]);

  const handleGamePackKeyDown = useCallback((packIndex: number, event: React.KeyboardEvent<HTMLElement>) => {
    if (!usesLengthBasedGamePackInput || event.key !== 'Backspace') {
      return;
    }

    const input = event.target as HTMLInputElement;
    const inputValue = typeof input?.value === 'string' ? input.value : '';
    if (!inputValue) {
      return;
    }

    const selectionStart = input.selectionStart ?? inputValue.length;
    const selectionEnd = input.selectionEnd ?? inputValue.length;
    const isCursorAtEnd = selectionStart === inputValue.length && selectionEnd === inputValue.length;
    if (!isCursorAtEnd) {
      return;
    }

    const nextGame = getVendingInventoryGameAfterAutoDashBackspace(inputValue, gameIdLength);
    if (nextGame === null) {
      return;
    }

    event.preventDefault();
    setValue(`${linePathBase}.game`, nextGame, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${linePathBase}.selected_packs.${packIndex}.pack_id`, null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [gameIdLength, linePathBase, setValue, usesLengthBasedGamePackInput]);

  const handleQuantityChange = useCallback((packIndex: number, rawValue: string) => {
    const nextQuantity = sanitizeVendingSalesIntegerInput(rawValue).slice(0, 6);
    setValue(`${linePathBase}.selected_packs.${packIndex}.remaining_qty`, nextQuantity, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [linePathBase, setValue]);

  const handleInsertBin = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (!canInsertInventoryBin || !onInsert) {
      return;
    }

    event.stopPropagation();
    onInsert(idx);
  }, [canInsertInventoryBin, idx, onInsert]);

  const handleAddPackToBin = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (!canManagePackSlots) {
      return;
    }

    event.stopPropagation();
    const nextSelectedPacks = appendInventorySelectedPackSlot(line);
    setValue(`${linePathBase}.selected_packs`, nextSelectedPacks, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${linePathBase}.required_pack_count`, nextSelectedPacks.length, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [canManagePackSlots, line, linePathBase, setValue]);

  const handleOpenPackMenu = useCallback(async (event: React.MouseEvent<HTMLElement>, packIndex: number) => {
    event.stopPropagation();
    setPackMenuAnchorEl(event.currentTarget);
    setActivePackIndex(packIndex);
    setPackRecommendationsError(null);

    const gameId = toOptionalNumber(line?.game);
    if (!queueId || !reportDate || gameId === null || !isInventoryReport) {
      return;
    }

    setPackRecommendationsLoading(true);
    try {
      const response = await auditService.getVendingInventoryPackRecommendations(queueId, {
        report_date: reportDate,
        game_id: gameId,
        bin: toOptionalNumber(line?.bin),
        quantity: toOptionalNumber(line?.selected_packs?.[packIndex]?.remaining_qty),
        value: toOptionalNumber(line?.value),
      });
      const nextCandidates = Array.isArray(response.candidates) ? response.candidates : [];
      setDynamicPackCandidates(nextCandidates);
      setValue(`${linePathBase}.pack_candidates`, nextCandidates, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    } catch (error: any) {
      setPackRecommendationsError(error?.response?.data?.detail || error?.message || 'Could not load pack recommendations');
    } finally {
      setPackRecommendationsLoading(false);
    }
  }, [isInventoryReport, line, linePathBase, queueId, reportDate, setValue]);

  const handleClosePackMenu = useCallback(() => {
    setPackMenuAnchorEl(null);
  }, []);

  const handleSelectPack = useCallback((packId: number | null | undefined) => {
    setValue(`${linePathBase}.selected_packs.${activePackIndex}.pack_id`, packId == null ? null : String(packId), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setPackMenuAnchorEl(null);
  }, [activePackIndex, linePathBase, setValue]);

  const handleClearSlot = useCallback((packIndex: number) => {
    if (slotCount <= 1) {
      remove(idx);
      return;
    }

    if (canManagePackSlots) {
      const nextSelectedPacks = removeInventorySelectedPackSlot(line, packIndex);
      setValue(`${linePathBase}.selected_packs`, nextSelectedPacks, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue(`${linePathBase}.required_pack_count`, nextSelectedPacks.length, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      return;
    }

    setValue(`${linePathBase}.selected_packs.${packIndex}.pack_id`, null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`${linePathBase}.selected_packs.${packIndex}.remaining_qty`, null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [canManagePackSlots, idx, line, linePathBase, remove, setValue, slotCount]);

  return (
    <React.Fragment key={`vending-inventory-line-${field.id}-${idx}`}>
      {packSlots.map((pack, packIndex) => {
        const quantityValue = pack.remaining_qty ?? '';
        const packIdValue = pack.pack_id ?? '';
        const computedSlotValue = getInventorySelectedPackDisplayValue(line, pack, gamesData);
        const showBin = packIndex === 0;
        const showRowDivider = packIndex === slotCount - 1;

        return (
          <Box
            key={`vending-inventory-line-${field.id}-${idx}-pack-${packIndex}`}
            onMouseEnter={() => handleRowHover(idx + 1)}
            onMouseLeave={() => handleRowHover(null)}
            sx={{
              display: 'grid',
              gridTemplateColumns: '64px minmax(196px, 1.08fr) 96px 104px 20px 20px',
              alignItems: 'center',
              px: 0,
              py: 0,
              height: OCR_GUIDE_ROW_HEIGHT,
              minHeight: OCR_GUIDE_ROW_HEIGHT,
              maxHeight: OCR_GUIDE_ROW_HEIGHT,
              bgcolor: 'transparent',
              position: 'relative',
              columnGap: 0.25,
              borderBottom: showRowDivider
                ? (isRowHovered ? '2px solid #3a3128' : '1px dashed #d6b982')
                : 'none',
              boxShadow: showRowDivider && isRowHovered ? '0 0 8px rgba(58, 49, 40, 0.25)' : 'none',
              fontFamily: 'inherit',
            }}
          >
            {canInsertInventoryBin && showBin && (
              <Tooltip title="Insert new bin" arrow placement="top">
                <IconButton
                  aria-label="Insert new bin"
                  onClick={handleInsertBin}
                  size="small"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: -10,
                    p: 0,
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    zIndex: 4,
                    opacity: isRowHovered ? 1 : 0,
                    color: '#2C6CA0',
                    transition: 'opacity 0.12s ease-in-out',
                  }}
                >
                  <Add sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
                px: 0,
                py: 0,
                display: 'flex',
                alignItems: 'center',
                height: OCR_GUIDE_ROW_HEIGHT,
                position: 'relative',
              }}
            >
              <TextField
                value={showBin ? lineBin : ''}
                onChange={(event) => {
                  if (!showBin) {
                    return;
                  }
                  setValue(`${linePathBase}.bin`, sanitizeVendingSalesIntegerInput(event.target.value).slice(0, 3), {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                }}
                disabled={!showBin}
                error={!!rowErrors?.bin}
                size="small"
                sx={mergeOutlinedInputStyles({
                  ...receiptOutlinedInputStyles,
                  width: '100%',
                  color: '#222B45',
                  ...buildOutlinedErrorBorderStyles(!!rowErrors?.bin),
                  '& .MuiInputBase-input': {
                    ...receiptOutlinedInputStyles['& .MuiInputBase-input'],
                    fontSize: 17,
                    fontFamily: '"Courier Prime", "Courier New", monospace',
                    fontWeight: 400,
                    color: '#222B45',
                    textAlign: 'center',
                    ...(showBin ? {} : {
                      WebkitTextFillColor: '#222B45',
                      opacity: 1,
                    }),
                  },
                })}
                inputProps={{ 'aria-label': 'Inventory bin' }}
                slotProps={{ inputLabel: { shrink: false } }}
              />
              {canManagePackSlots && showBin && (
                <Tooltip title="Add pack to this bin" arrow placement="top">
                  <IconButton
                    aria-label="Add pack to this bin"
                    onClick={handleAddPackToBin}
                    size="small"
                    sx={{
                      position: 'absolute',
                      right: 0,
                      bottom: 0,
                      opacity: isRowHovered ? 1 : 0,
                      pointerEvents: isRowHovered ? 'auto' : 'none',
                      transition: 'opacity 0.12s ease-in-out, background-color 0.12s ease-in-out',
                      color: '#1B7F3A',
                      bgcolor: 'rgba(218, 247, 226, 0.95)',
                      border: '1px solid #69B77B',
                      padding: 0,
                      width: '17px',
                      height: '17px',
                      minWidth: '17px',
                      minHeight: '17px',
                      maxWidth: '17px',
                      maxHeight: '17px',
                      zIndex: 4,
                      '&:hover': {
                        opacity: 1,
                        bgcolor: '#C8EFD2',
                      },
                    }}
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', height: OCR_GUIDE_ROW_HEIGHT }}>
              <TextField
                value={usesLengthBasedGamePackInput
                  ? buildVendingInventoryGamePackValue(lineGame, packIdValue, gameIdLength)
                  : buildLegacyVendingInventoryGamePackValue(lineGame, packIdValue)}
                onChange={(event) => handlePackInputChange(packIndex, event.target.value)}
                onKeyDown={(event) => handleGamePackKeyDown(packIndex, event)}
                error={!!rowErrors?.game}
                size="small"
                sx={mergeOutlinedInputStyles({
                  ...receiptOutlinedInputStyles,
                  width: '100%',
                  color: '#222B45',
                  ...buildOutlinedErrorBorderStyles(!!rowErrors?.game),
                  '& .MuiInputBase-input': {
                    ...receiptOutlinedInputStyles['& .MuiInputBase-input'],
                    fontSize: 17,
                    fontFamily: '"Courier Prime", "Courier New", monospace',
                    fontWeight: 400,
                    color: '#222B45',
                    textAlign: 'left',
                    paddingLeft: '8px !important',
                    paddingRight: '24px !important',
                  },
                })}
                inputProps={{ 'aria-label': 'Inventory game-pack' }}
                slotProps={{ inputLabel: { shrink: false } }}
              />
              {isInventoryReady && (
                <IconButton
                  size="small"
                  onClick={(event) => handleOpenPackMenu(event, packIndex)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    p: 0,
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    color: '#2C6CA0',
                    zIndex: 2,
                  }}
                >
                  <KeyboardArrowDown sx={{ fontSize: 22 }} />
                </IconButton>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, height: '100%', width: '100%' }}>
              <TextField
                value={quantityValue}
                onChange={(event) => handleQuantityChange(packIndex, event.target.value)}
                error={!!rowErrors?.quantity}
                size="small"
                sx={mergeOutlinedInputStyles({
                  ...receiptOutlinedInputStyles,
                  width: 'calc(100% - 44px)',
                  flex: '0 0 calc(100% - 44px)',
                  color: '#222B45',
                  ...buildOutlinedErrorBorderStyles(!!rowErrors?.quantity),
                  '& .MuiInputBase-input': {
                    ...receiptOutlinedInputStyles['& .MuiInputBase-input'],
                    fontSize: 17,
                    fontFamily: '"Courier Prime", "Courier New", monospace',
                    fontWeight: 400,
                    color: '#222B45',
                    textAlign: 'right',
                    paddingRight: '4px !important',
                  },
                })}
                inputProps={{ 'aria-label': 'Inventory quantity' }}
                slotProps={{ inputLabel: { shrink: false } }}
              />
              <Box
                sx={{
                  flex: '0 0 44px',
                  width: 44,
                  height: OCR_GUIDE_ROW_HEIGHT - 2,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 0.25,
                  opacity: isRowHovered ? 1 : 0,
                  pointerEvents: isRowHovered ? 'auto' : 'none',
                  transition: 'opacity 0.12s ease-in-out',
                  zIndex: 3,
                }}
              >
                <IconButton
                  aria-label="Decrease inventory quantity"
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    const currentQuantity = toOptionalNumber(quantityValue) ?? 0;
                    handleQuantityChange(packIndex, String(Math.max(0, currentQuantity - 1)));
                  }}
                  sx={{
                    p: 0,
                    width: 22,
                    height: 22,
                    minWidth: 22,
                    bgcolor: 'rgba(255, 250, 240, 0.95)',
                    border: '1px solid #b98542',
                    borderRadius: 0.75,
                    color: '#2b2825',
                    '&:hover': { bgcolor: '#f4e6cc' },
                  }}
                >
                  <KeyboardArrowDown sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  aria-label="Increase inventory quantity"
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    const currentQuantity = toOptionalNumber(quantityValue) ?? 0;
                    handleQuantityChange(packIndex, String(currentQuantity + 1));
                  }}
                  sx={{
                    p: 0,
                    width: 22,
                    height: 22,
                    minWidth: 22,
                    bgcolor: 'rgba(255, 250, 240, 0.95)',
                    border: '1px solid #b98542',
                    borderRadius: 0.75,
                    color: '#2b2825',
                    '&:hover': { bgcolor: '#f4e6cc' },
                  }}
                >
                  <KeyboardArrowUp sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <TextField
                value={Number.isFinite(computedSlotValue) ? computedSlotValue.toFixed(2) : '0.00'}
                placeholder="0.00"
                size="small"
                disabled
                error={!!rowErrors?.value}
                sx={mergeOutlinedInputStyles({
                  ...receiptOutlinedInputStyles,
                  width: '100%',
                  color: '#222B45',
                  ...buildOutlinedErrorBorderStyles(!!rowErrors?.value),
                  '& .MuiInputBase-input': {
                    ...receiptOutlinedInputStyles['& .MuiInputBase-input'],
                    fontSize: 17,
                    fontFamily: '"Courier Prime", "Courier New", monospace',
                    fontWeight: 400,
                    color: '#222B45',
                    textAlign: 'right',
                    WebkitTextFillColor: '#222B45',
                  },
                })}
                slotProps={{ inputLabel: { shrink: false } }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              {isNewPack && (
                <Tooltip title="New pack: not present in previous inventory" arrow placement="top">
                  <Box
                    component="span"
                    sx={{
                      width: 20,
                      height: 20,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#2C6CA0',
                    }}
                  >
                    <InfoOutlined sx={{ fontSize: 18 }} />
                  </Box>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ minWidth: 24, width: 24, maxWidth: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0 }}>
              <IconButton
                aria-label={slotCount <= 1 ? 'Remove inventory row' : 'Clear inventory pack'}
                onClick={() => handleClearSlot(packIndex)}
                size="small"
                sx={{
                  opacity: isRowHovered ? 1 : 0.7,
                  transition: 'opacity 0.2s',
                  color: 'rgba(34, 43, 69, 1)',
                  '&:hover': { opacity: 1 },
                  '&.Mui-disabled': { opacity: 0.3 },
                  padding: 0,
                  width: '17px',
                  height: '17px',
                  minWidth: '17px',
                  minHeight: '17px',
                  maxWidth: '17px',
                  maxHeight: '17px',
                }}
              >
                <RemoveCircleOutline sx={{ fontSize: 17 }} />
              </IconButton>
            </Box>
          </Box>
        );
      })}

      <Menu
        anchorEl={packMenuAnchorEl}
        open={Boolean(packMenuAnchorEl)}
        onClose={handleClosePackMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {packRecommendationsLoading ? (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              Loading packs
            </Box>
          </MenuItem>
        ) : packRecommendationsError ? (
          <MenuItem disabled>{packRecommendationsError}</MenuItem>
        ) : packCandidates.length > 0 ? (
          packCandidates.map((candidate, optionIndex) => (
            <MenuItem
              key={`inventory-pack-candidate-${idx}-${activePackIndex}-${optionIndex}-${candidate?.pack_id ?? 'na'}`}
              onClick={() => handleSelectPack(candidate?.pack_id)}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {`${candidate?.pack_id ?? '-'} (${formatPackCandidateLocation(candidate)})`}
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: '#374151',
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`${candidate?.status ?? '-'} | Set ${formatPackCandidateDate(candidate?.settled_date ?? candidate?.status_date)} | Act ${formatPackCandidateDate(candidate?.activated_date)} | Rem ${candidate?.latest_remaining_qty ?? '-'}`}
                </Box>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No packs</MenuItem>
        )}
      </Menu>
    </React.Fragment>
  );
});

VendingInventoryMultiPackRows.displayName = 'VendingInventoryMultiPackRows';

export default VendingInventoryMultiPackRows;
